import { t } from '@transifex/native';
import { T, useT } from '@transifex/react';
import {
  Link,
  Form,
  useActionData,
  useLoaderData,
  useSearchParams,
  useNavigation,
} from 'react-router';
import { Button, buttonVariants } from '~/components/ui/button';
import { useForm, getFormProps, getInputProps } from '@conform-to/react';
import { parseWithZod } from '@conform-to/zod';
import { z } from 'zod';
import { passwordSchema } from '~/lib/validation';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Checkbox } from '../ui/checkbox';
import { usePasswordVisibility } from '~/lib/usePasswordVisibility';
import { PasswordVisibilityButton } from '~/components/ui/password-visibility-icon';
import { FormErrors } from '../ui/form-error';
import { FormGroup } from '../ui/form-ui';
import { cn } from '~/lib/utils';
import { ampli } from '~/third-party/ampli/.client';
import { PasswordRequirements } from './password-requirements';
import { useFeatureFlag } from '~/contexts/feature-flag-context';
import { emailSchema } from '~/lib/validation';
import type { Route } from '../../routes/signup/+types/signup';

export const signUpEmailStepSchema = emailSchema.extend({
  terms: z.literal('on', {
    errorMap: function () {
      return { message: t('You must agree to the terms') };
    },
  }),
});

export const signUpSchema = signUpEmailStepSchema.extend({
  password: passwordSchema,
});

function TermsLabel() {
  return (
    <span className="-mt-1 text-body-big">
      <T
        _str={
          'By signing up, you acknowledge and agree to {flpTos}, and our {privacy}. Please review these documents carefully before proceeding.'
        }
        flpTos={
          <a
            className="text-primary underline"
            target="_blank"
            rel="noopener noreferrer"
            href="https://kotobase.io/terms/fujitsu-launchpad-terms-of-service"
          >
            <T _str="Fujitsu Launchpad's Terms of Service" />
          </a>
        }
        privacy={
          <a
            className="text-primary underline"
            target="_blank"
            rel="noopener noreferrer"
            href="https://kotobase.io/privacy-policy/privacy-policy"
          >
            <T _str="Privacy Policy" />
          </a>
        }
      />
    </span>
  );
}

/** Dispatch component — picks the appropriate step based on feature flag + URL */
function SignUp() {
  const ssoEnabled = useFeatureFlag('sso');
  const loaderData = useLoaderData<Route.ComponentProps['loaderData']>();
  const [searchParams] = useSearchParams();
  const stepParam = searchParams.get('step');

  if (!ssoEnabled) {
    return <LegacySignUpForm />;
  }

  const email = loaderData?.ssoSignupEmail ?? '';

  if (stepParam === 'password' && email) {
    return <SignUpPasswordStep email={email} />;
  }

  return <SignUpEmailStep defaultEmail={email} />;
}

function SignUpEmailStep({ defaultEmail }: { defaultEmail: string }) {
  const t = useT();
  const actionData = useActionData();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === 'submitting';

  const [form, fields] = useForm({
    onValidate: function ({ formData }: { formData: FormData }) {
      return parseWithZod(formData, { schema: signUpEmailStepSchema });
    },
    shouldValidate: 'onInput',
    shouldRevalidate: 'onInput',
    lastResult: actionData,
  });

  return (
    <>
      <Form
        className="flex w-full flex-col gap-6"
        {...getFormProps(form)}
        noValidate
        method="post"
        action="/signup"
      >
        <input type="hidden" name="intent" value="discover" />
        <FormGroup>
          <Label htmlFor={fields.email.id}>
            <T _str="Email" />
          </Label>
          <Input
            {...getInputProps(fields.email, { type: 'email' })}
            placeholder={t('Your email address')}
            defaultValue={defaultEmail}
          />
          <FormErrors errors={fields.email.errors} />
        </FormGroup>
        <div className="flex w-full items-start gap-2">
          <Checkbox
            id={fields.terms.id}
            name={fields.terms.name}
            required
            aria-invalid={fields.terms.errors?.length ? 'true' : undefined}
            aria-describedby={fields.terms.errors?.length ? `${fields.terms.id}-error` : undefined}
            className="mt-1"
          />
          <Label htmlFor={fields.terms.id} className="leading-relaxed">
            <TermsLabel />
          </Label>
        </div>
        <FormErrors errors={fields.terms.errors} />
        <FormErrors errors={form.errors} />
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <T _str="Checking..." /> : <T _str="Continue" />}
        </Button>
      </Form>
      <LogInLink />
    </>
  );
}

function SignUpPasswordStep({ email }: { email: string }) {
  const t = useT();
  const actionData = useActionData();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === 'submitting';
  const [showPassword, togglePasswordVisibility] = usePasswordVisibility();

  const [form, fields] = useForm({
    onValidate: function ({ formData }: { formData: FormData }) {
      return parseWithZod(formData, { schema: signUpSchema });
    },
    onSubmit: function () {
      ampli.clickSignUp();
    },
    shouldValidate: 'onInput',
    shouldRevalidate: 'onInput',
    lastResult: actionData,
  });

  return (
    <>
      <Form
        className="flex w-full flex-col gap-6"
        {...getFormProps(form)}
        noValidate
        method="post"
        action="/signup?step=password"
      >
        <input type="hidden" name="email" value={email} />
        <input type="hidden" name="terms" value="on" />
        <FormGroup>
          <Label>
            <T _str="Email" />
          </Label>
          <div className="text-text-600 flex items-center gap-2 text-body-big">
            <span>{email}</span>
            <Link to="/signup" className="text-green-500 underline">
              <T _str="Change" />
            </Link>
          </div>
        </FormGroup>
        <FormGroup>
          <Label htmlFor={fields.password.id}>
            <T _str="Password" />
          </Label>
          <div className="relative flex items-center">
            <Input
              {...getInputProps(fields.password, { type: showPassword ? 'text' : 'password' })}
              placeholder={t('Set a password')}
              className="pr-10"
            />
            <PasswordVisibilityButton show={showPassword} onClick={togglePasswordVisibility} />
          </div>
          <PasswordRequirements errors={fields.password.errors} />
        </FormGroup>
        <FormErrors errors={form.errors} />
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <T _str="Signing up..." /> : <T _str="Sign up" />}
        </Button>
      </Form>
      <LogInLink />
    </>
  );
}

function LegacySignUpForm() {
  const t = useT();
  const actionData = useActionData();
  const [showPassword, togglePasswordVisibility] = usePasswordVisibility();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === 'submitting';

  const [form, fields] = useForm({
    onValidate: function ({ formData }: { formData: FormData }) {
      return parseWithZod(formData, { schema: signUpSchema });
    },
    onSubmit: () => {
      ampli.clickSignUp();
    },
    shouldValidate: 'onInput',
    shouldRevalidate: 'onInput',
    lastResult: actionData,
  });

  return (
    <Form
      className="flex w-full flex-col gap-6"
      {...getFormProps(form)}
      noValidate
      method="post"
      action="/signup"
    >
      <FormGroup>
        <Label htmlFor={fields.email.id}>
          <T _str="Email" />
        </Label>
        <Input
          {...getInputProps(fields.email, { type: 'email' })}
          placeholder={t('Your email address')}
        />
        <FormErrors errors={fields.email.errors} />
      </FormGroup>
      <FormGroup>
        <Label htmlFor={fields.password.id}>
          <T _str="Password" />
        </Label>
        <div className="relative flex items-center">
          <Input
            {...getInputProps(fields.password, { type: showPassword ? 'text' : 'password' })}
            placeholder={t('Set a password')}
            className="pr-10"
          />
          <PasswordVisibilityButton show={showPassword} onClick={togglePasswordVisibility} />
        </div>
        <PasswordRequirements errors={fields.password.errors} />
      </FormGroup>
      <div className="flex w-full items-start gap-2">
        <Checkbox
          id={fields.terms.id}
          name={fields.terms.name}
          required
          aria-invalid={fields.terms.errors?.length ? 'true' : undefined}
          aria-describedby={fields.terms.errors?.length ? `${fields.terms.id}-error` : undefined}
          className="mt-1"
        />
        <Label htmlFor={fields.terms.id} className="leading-relaxed">
          <TermsLabel />
        </Label>
      </div>
      <FormErrors errors={fields.terms.errors} />
      <FormErrors errors={form.errors} />
      <Button type="submit" disabled={isSubmitting}>
        {isSubmitting ? <T _str="Signing up..." /> : <T _str="Sign up" />}
      </Button>
      <LogInLink />
    </Form>
  );
}

function LogInLink() {
  return (
    <Link
      className={cn(
        buttonVariants({ variant: 'underlined' }),
        'wrap-break-word whitespace-pre-wrap'
      )}
      to="/login"
    >
      <T _str="Already have an account? Log In" />
    </Link>
  );
}

export { SignUp };
